import type { PropsWithChildren } from "react";
import { createContext, useMemo } from "react";
import type { WorkflowActivity, WorkflowDetails } from "../../../../../api/types/workflowTypes";
import type { OpenFieldCitation, WorkflowStep } from "./workflowDetailsHelpers";

interface ContextValue {
  workflowDetails: WorkflowDetails;
  steps: WorkflowStep[];
  selectedStepIndex: number;
  selectStep: (index: number) => void;
  selectedStep?: WorkflowStep;
  selectedActivity?: WorkflowActivity;
  openFieldCitation?: OpenFieldCitation;
  viewCitations: (activityId: string, documentName: string, fieldId?: string, selectedCitationId?: string) => void;
  currentTemplateVersionId?: string;
}

const WorkflowRunDetailsContext = createContext<ContextValue | undefined>(undefined);

interface Props {
  workflowDetails: WorkflowDetails;
  steps: WorkflowStep[];
  selectedStepIndex: number;
  selectStep: (index: number) => void;
  openFieldCitation?: OpenFieldCitation;
  viewCitations: (activityId: string, documentName: string, fieldId?: string, selectedCitationId?: string) => void;
  currentTemplateVersionId?: string;
}

export const WorkflowRunDetailsContextProvider = ({
  children,
  workflowDetails,
  steps,
  selectedStepIndex,
  selectStep,
  openFieldCitation,
  viewCitations,
  currentTemplateVersionId,
}: PropsWithChildren<Props>) => {
  const value = useMemo<ContextValue>(() => {
    const selectedStep = steps[selectedStepIndex];
    const selectedActivity = selectedStep?.type === "activity" ? selectedStep.activity : undefined;

    return {
      workflowDetails,
      steps,
      selectedStepIndex,
      selectStep,
      selectedStep,
      selectedActivity,
      openFieldCitation,
      viewCitations,
      currentTemplateVersionId,
    };
  }, [
    workflowDetails,
    steps,
    selectedStepIndex,
    selectStep,
    openFieldCitation,
    viewCitations,
    currentTemplateVersionId,
  ]);

  return <WorkflowRunDetailsContext value={value}>{children}</WorkflowRunDetailsContext>;
};

export default WorkflowRunDetailsContext;
